import { useRouter } from 'next/router';
import React, { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { Text } from '@mantine/core';
import { useWindowEvent } from '@mantine/hooks';

import { DatasheetMode, useDatasheetMode } from 'components/datasheet/DatasheetMode';
import { ModalService } from 'services/ModalService';

const EDITING_MODES = [DatasheetMode.EDIT, DatasheetMode.CREATE, DatasheetMode.DUPLICATE];

const UnsavedChangesGuard = () => {
    const router = useRouter();
    const mode = useDatasheetMode();
    const {
        formState: { isDirty },
    } = useFormContext();

    const shouldBlock = EDITING_MODES.includes(mode) && isDirty;
    const confirmedNavigation = useRef(false);

    useWindowEvent('beforeunload', (event) => {
        if (shouldBlock) {
            event.preventDefault();
            event.returnValue = ''; // for older browsers
        }
    });

    useEffect(() => {
        if (!shouldBlock) return;
        if (mode === DatasheetMode.DUPLICATE) return;

        const handleRouteChangeStart = (url: string) => {
            if (confirmedNavigation.current) return;

            ModalService.openConfirmModal({
                centered: true,
                title: 'Unsaved changes',
                children: (
                    <Text size="sm">You have unsaved changes. If you leave this page, your changes will be lost.</Text>
                ),
                labels: { confirm: 'Leave without saving', cancel: 'Stay on page' },
                confirmProps: { color: 'red' },
                onConfirm: () => {
                    confirmedNavigation.current = true;
                    router.push(url);
                },
            });

            router.events.emit('routeChangeError');
            // Aborts the in-progress navigation so the confirmation can be awaited.
            throw 'Route change aborted to confirm unsaved changes.';
        };

        router.events.on('routeChangeStart', handleRouteChangeStart);

        return () => {
            router.events.off('routeChangeStart', handleRouteChangeStart);
        };
    }, [shouldBlock, router]);

    return null;
};

export { UnsavedChangesGuard };
